import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

export default function Validation() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-6">
      <SlideHeader eyebrow="Validation">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-5xl md:leading-[1.1]">
          Apptonomy is live in production with paying customers and logos +
          testimonials accruing.
        </h1>
        <p className="mt-3 text-lg font-medium tracking-tight text-slate-700 md:mt-4 md:text-2xl">
          Production usage, named partners, first revenue, and a closing
          pipeline — the state of proof today.
        </p>
      </SlideHeader>

      {/* Tier 1 — the platform is genuinely running */}
      <div className="grid grid-cols-3 gap-3 md:gap-5">
        <MetricTile value="143" label="Monthly active accounts" />
        <MetricTile value="191" label="Projects under management" />
        <MetricTile value=">20,000" label="ASO audits run in production" />
      </div>

      {/* Tier 2 — the proof ladder: who uses it, who pays, what's closing */}
      <div className="mt-5 grid grid-cols-1 gap-4 md:mt-6 md:flex-1 md:grid-cols-3 md:gap-5">
        <Column label="Adoption">
          <Fact value="10" sub="design partners">
            Publishers, ASO professionals and agency operators across gaming,
            health &amp; fitness, lifestyle, fintech — an active feedback loop
            shaping the roadmap.
          </Fact>
          <Fact value="~70" sub="beta users">
            Self-serve accounts on Free + Basic running audits on their own
            apps.
          </Fact>
          <Fact value="5 + 20" sub="evangelists + affiliates">
            Senior ASO practitioners advocating in their networks; 20
            affiliates approved on Rewardful.
          </Fact>
        </Column>

        <Column label="Commercial" accent>
          <Fact value="[N]" sub="paying customers" accent>
            Pickem public since May 2026; the others not yet nameable.
          </Fact>
          <Fact value="Geeklab" sub="partnership agreed" accent>
            Logos, case studies and referral cross-marketing.
          </Fact>
          <Fact value="Terminal 4" sub="logo confirmed" accent>
            Confirmed by their CEO.
          </Fact>
          <Fact value="Asapty" sub="cross-function partnership" accent>
            Complementary paid-UA tooling — a signal for platform-level
            partnerships.
          </Fact>
        </Column>

        <Column label="Pipeline">
          <Fact value="14" sub="priority opportunities">
            Active BD conversations across gaming publishers, ASO agencies and
            enterprise consumer brands.
          </Fact>
          <Fact value="3" sub="LOIs in motion">
            Nord and Kilo in legal review, IGG progressing.
          </Fact>
          <Fact value="3 + 3" sub="locked logos + high-probability deals">
            Enterprise trials running with top mobile-game publishers and a
            major consumer-app publisher under NDA.
          </Fact>
        </Column>
      </div>
    </div>
  );
}

function MetricTile({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200 md:p-5">
      <p className="text-3xl font-bold leading-none tracking-tight text-blue-700 md:text-4xl">
        {value}
      </p>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-900 md:text-sm">
        {label}
      </p>
    </div>
  );
}

function Column({
  label,
  accent,
  children,
}: {
  label: string;
  accent?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex flex-col rounded-xl p-4 ring-1 md:p-5 ${
        accent ? "bg-blue-50 ring-blue-300" : "bg-white ring-slate-200"
      }`}
    >
      <p
        className={`text-xs font-bold uppercase tracking-[0.18em] md:text-sm ${
          accent ? "text-blue-700" : "text-slate-500"
        }`}
      >
        {label}
      </p>
      <div className="mt-3 flex flex-col gap-3 md:gap-3.5">{children}</div>
    </div>
  );
}

function Fact({
  value,
  sub,
  accent,
  children,
}: {
  value: string;
  sub: string;
  accent?: boolean;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="flex flex-wrap items-baseline gap-x-2">
        <span
          className={`text-lg font-bold tracking-tight md:text-xl ${
            accent ? "text-blue-700" : "text-slate-900"
          }`}
        >
          {value}
        </span>
        <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 md:text-[13px]">
          {sub}
        </span>
      </p>
      <p className="mt-1 text-xs leading-snug text-slate-600 md:text-sm">
        {children}
      </p>
    </div>
  );
}
